import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const TIER_COLORS = {
  platinum: 'bg-purple-100 text-purple-700',
  gold: 'bg-yellow-100 text-yellow-700',
  silver: 'bg-slate-100 text-slate-600',
  bronze: 'bg-orange-100 text-orange-700',
};

const PartnerSelectorHeader = ({
  partners = [],
  selectedPartner,
  selectedPartnerId,
  onSelectPartner,
  productCount = 0,
  serviceCount = 0,
  bundleCount = 0,
  completenessScore = 0,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = partners?.filter(p =>
    (p?.legal_name || p?.brand_name || '')?.toLowerCase()?.includes(search?.toLowerCase()) ||
    (p?.brand_name || '')?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const displayName = selectedPartner?.brand_name || selectedPartner?.legal_name || 'Select a Partner';
  const tier = selectedPartner?.tier;

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-500';
  };

  const getScoreBarColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="sticky top-20 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
      <div className="max-w-[1600px] mx-auto px-24 lg:px-32 py-16">
        <div className="flex flex-col lg:flex-row lg:items-center gap-16">
          {/* Partner Selector */}
          <div className="relative flex-shrink-0" ref={dropdownRef}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-12 px-16 py-12 bg-white border-2 border-primary/30 rounded-xl hover:border-primary transition-all min-w-[280px] shadow-sm"
            >
              <div className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg">
                <Icon name="Building2" size={18} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-xs text-slate-500 font-medium">Active Partner</div>
                <div className="text-sm font-semibold text-slate-900 truncate max-w-[180px]">
                  {loading ? 'Loading...' : displayName}
                </div>
              </div>
              {tier && (
                <span className={`px-8 py-3 rounded-full text-xs font-semibold capitalize ${TIER_COLORS?.[tier] || 'bg-slate-100 text-slate-600'}`}>
                  {tier}
                </span>
              )}
              <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-slate-400 flex-shrink-0" />
            </button>

            {isOpen && (
              <div className="absolute top-full left-0 mt-8 w-[340px] bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
                <div className="p-12 border-b border-slate-100">
                  <div className="relative">
                    <Icon name="Search" size={14} className="absolute left-10 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search partners..."
                      value={search}
                      onChange={(e) => setSearch(e?.target?.value)}
                      className="w-full pl-32 pr-12 py-8 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-[280px] overflow-y-auto">
                  {filtered?.length === 0 ? (
                    <div className="p-16 text-center text-sm text-slate-500">No partners found</div>
                  ) : (
                    filtered?.map(partner => (
                      <button
                        key={partner?.id}
                        onClick={() => {
                          onSelectPartner(partner?.id);
                          setIsOpen(false);
                          setSearch('');
                        }}
                        className={`w-full flex items-center gap-12 px-16 py-12 hover:bg-slate-50 transition-colors text-left ${partner?.id === selectedPartnerId ? 'bg-primary/5' : ''}`}
                      >
                        <div className="w-36 h-36 rounded-lg bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-primary">
                            {(partner?.brand_name || partner?.legal_name || '?')?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                            {partner?.brand_name || partner?.legal_name}
                          </div>
                          <div className="text-xs text-slate-500 truncate">{partner?.partner_type || partner?.type}</div>
                        </div>
                        {partner?.tier && (
                          <span className={`px-8 py-3 rounded-full text-xs font-semibold capitalize flex-shrink-0 ${TIER_COLORS?.[partner?.tier] || 'bg-slate-100 text-slate-600'}`}>
                            {partner?.tier}
                          </span>
                        )}
                        {partner?.id === selectedPartnerId && (
                          <Icon name="Check" size={14} className="text-primary flex-shrink-0" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quick Stats — only when partner selected */}
          {selectedPartner ? (
            <div className="flex flex-wrap items-center gap-12 flex-1">
              <div className="flex items-center gap-8 px-14 py-10 bg-blue-50 rounded-xl border border-blue-100">
                <Icon name="Package" size={16} className="text-blue-600" />
                <div>
                  <div className="text-lg font-bold text-blue-700 leading-none">{productCount}</div>
                  <div className="text-xs text-blue-500 mt-1">Products</div>
                </div>
              </div>
              <div className="flex items-center gap-8 px-14 py-10 bg-teal-50 rounded-xl border border-teal-100">
                <Icon name="Wrench" size={16} className="text-teal-600" />
                <div>
                  <div className="text-lg font-bold text-teal-700 leading-none">{serviceCount}</div>
                  <div className="text-xs text-teal-500 mt-1">Services</div>
                </div>
              </div>
              <div className="flex items-center gap-8 px-14 py-10 bg-purple-50 rounded-xl border border-purple-100">
                <Icon name="Layers" size={16} className="text-purple-600" />
                <div>
                  <div className="text-lg font-bold text-purple-700 leading-none">{bundleCount}</div>
                  <div className="text-xs text-purple-500 mt-1">Bundles</div>
                </div>
              </div>

              {/* Completeness Score */}
              <div className="flex items-center gap-10 px-14 py-10 bg-slate-50 rounded-xl border border-slate-200 min-w-[180px]">
                <Icon name="BarChart2" size={16} className="text-slate-500 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-slate-500">Portfolio Completeness</span>
                    <span className={`text-sm font-bold ${getScoreColor(completenessScore)}`}>{completenessScore}%</span>
                  </div>
                  <div className="w-full h-4 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${getScoreBarColor(completenessScore)}`}
                      style={{ width: `${completenessScore}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-10 px-16 py-12 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
              <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
              <span>Select a partner above to begin mapping their portfolio</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerSelectorHeader;
